/**
 * The account screen's lists, shared with their full pages (`/admin/:list`). On the account
 * screen each shows only its newest few, so no one list can take over the page; the header's
 * "See all" goes to the rest.
 */

import { Link } from 'react-router-dom';
import type { PoolQuestion } from '@games/jeopardy/ui/content';
import { api, type FormatSummary, type PackSummary, type SessionSummary } from '../lib/api';

/** How many rows a list shows on the account screen. */
export const ACCOUNT_SHOWN = 5;

/** The panel's title, and "See all N" when `shown` is fewer than `total`. */
export function PanelHead({ title, total, shown, to }: { title: string; total: number; shown: number; to: string }) {
  return (
    <div className="row" style={{ alignItems: 'baseline' }}>
      <h2 className="panel-title">{title}</h2>
      <span className="spacer" />
      {shown < total ? (
        <Link className="btn btn-sm btn-ghost" to={to}>
          See all {total}
        </Link>
      ) : null}
    </div>
  );
}

const STAGE_WORDS: Record<string, string> = {
  lobby: 'not started',
  segment: 'playing',
  interstitial: 'between rounds',
  results: 'finished',
};

/** Shows this account owns, newest first: what it hosted, from any device. */
export function HostedList({
  sessions,
  gameNames,
  limit,
}: {
  sessions: readonly SessionSummary[];
  gameNames: ReadonlyMap<string, string>;
  limit?: number;
}) {
  const shown = sessions.slice(0, limit);
  return (
    <div className="panel col" id="hosted">
      <PanelHead title="Games I hosted" total={sessions.length} shown={shown.length} to="/admin/hosted" />
      {sessions.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          None yet. <Link to="/create">Forge a game</Link> to host one.
        </p>
      ) : (
        <ul className="game-list">
          {shown.map((s) => (
            <li key={s.id}>
              <span className="code-chip">{s.code}</span>
              <span className="col" style={{ gap: 0, minWidth: 0 }}>
                <strong>{s.title}</strong>
                <span className="faint">
                  {s.segments > 1 ? `${s.segments} rounds` : (gameNames.get(s.gameId) ?? s.gameId)} ·{' '}
                  {s.playerCount} player{s.playerCount === 1 ? '' : 's'} · {STAGE_WORDS[s.stage] ?? s.stage}
                </span>
              </span>
              <span className="spacer" />
              <Link className="btn btn-sm" to={`/host/${s.code}`}>
                Open
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Ask, then delete. True if it went; a refusal goes to `onError`. */
export async function deleteTemplate(format: FormatSummary, onError: (message: string) => void): Promise<boolean> {
  if (!confirm(`Delete the template "${format.title}"? Games already started from it keep playing.`)) return false;
  try {
    await api.deleteFormat(format.id);
    return true;
  } catch (err) {
    onError(err instanceof Error ? err.message : 'Could not delete that template.');
    return false;
  }
}

export function TemplatesPanel({
  formats,
  onDelete,
  limit,
}: {
  formats: readonly FormatSummary[];
  onDelete: (format: FormatSummary) => void;
  limit?: number;
}) {
  const shown = formats.slice(0, limit);
  return (
    <div className="panel col" id="templates">
      <PanelHead title="My show templates" total={formats.length} shown={shown.length} to="/admin/templates" />
      {formats.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          None yet. Build one from <Link to="/create/custom/new">Forge a custom show</Link>.
        </p>
      ) : (
        <ul className="game-list">
          {shown.map((format) => (
            <li key={format.id}>
              <span className="col" style={{ gap: 0, minWidth: 0 }}>
                <strong>{format.title}</strong>
                <span className="faint">
                  {format.steps.length} round{format.steps.length === 1 ? '' : 's'} · {format.category} · ~
                  {format.estimatedMinutes} min
                </span>
              </span>
              <span className="spacer" />
              <button className="btn btn-sm btn-ghost" title="Delete template" onClick={() => onDelete(format)}>
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PacksPanel({
  packs,
  gameNames,
  limit,
}: {
  packs: readonly PackSummary[];
  gameNames: ReadonlyMap<string, string>;
  limit?: number;
}) {
  const shown = packs.slice(0, limit);
  return (
    <div className="panel col" id="packs">
      <PanelHead title="My clue packs" total={packs.length} shown={shown.length} to="/admin/packs" />
      {packs.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          No packs you own yet. Build a board to create one.
        </p>
      ) : (
        <ul className="game-list">
          {shown.map((pack) => (
            <li key={pack.id}>
              <span className="col" style={{ gap: 0, minWidth: 0 }}>
                <strong>{pack.title}</strong>
                <span className="faint">
                  {gameNames.get(pack.gameId) ?? pack.gameId} · {pack.itemCount} clue{pack.itemCount === 1 ? '' : 's'}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A few of this account's questions; the bank page is the full list (and the editor). */
export function QuestionsPanel({ questions, limit }: { questions: readonly PoolQuestion[] | null; limit: number }) {
  const shown = (questions ?? []).slice(0, limit);
  return (
    <div className="panel col" id="questions">
      <PanelHead title="My questions" total={questions?.length ?? 0} shown={shown.length} to="/questions" />
      {questions === null ? (
        <p className="muted" style={{ margin: 0 }}>
          Loading…
        </p>
      ) : questions.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          None in the bank yet.
        </p>
      ) : (
        <ul className="game-list">
          {shown.map((q) => (
            <li key={q.id}>
              <span className="col" style={{ gap: 0, minWidth: 0 }}>
                <strong className="clamp-1">{q.prompt}</strong>
                <span className="faint clamp-1">{q.tags.length > 0 ? q.tags.join(' · ') : 'No category'}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="row">
        <Link className="btn btn-sm" to="/questions">
          Open the bank
        </Link>
        <Link className="btn btn-sm btn-ghost" to="/questions?new=1">
          Add one
        </Link>
      </div>
    </div>
  );
}
